"use client";

/**
 * ColorPicker Component
 *
 * A color picker supporting 770 unique color combinations:
 * - 22 Tailwind color families × 7 shades (300-900) = 154 base colors
 * - 5 pattern tiers: solid, stripe, dots, grid, hatch
 *
 * Display logic:
 * - Always shows a fixed square grid (GRID_SIZE × GRID_SIZE)
 * - Available solids shown first, then backfills with patterned variants
 * - No tabs — patterns appear automatically as solids get consumed
 *
 * Auto-assign logic (getRandomColor):
 * - Uses full range: all patterns for all colors
 *
 * Usage:
 *   <ColorPicker value={color} onChange={setColor} usedColors={usedColors} />
 *   const newColor = getRandomColor(usedColors)
 *
 * Value format: "hex|pattern" (e.g., "#1d4ed8|solid")
 */

import type React from "react";
import { type FC, useMemo, useState } from "react";
import colors from "tailwindcss/colors";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

// ============================================================================
// TYPES
// ============================================================================

type ColorName =
  | "slate"
  | "gray"
  | "zinc"
  | "neutral"
  | "stone"
  | "red"
  | "orange"
  | "amber"
  | "yellow"
  | "lime"
  | "green"
  | "emerald"
  | "teal"
  | "cyan"
  | "sky"
  | "blue"
  | "indigo"
  | "violet"
  | "purple"
  | "fuchsia"
  | "pink"
  | "rose";

type ColorShade = "300" | "400" | "500" | "600" | "700" | "800" | "900";

export type PatternType = "solid" | "stripe" | "dots" | "grid" | "hatch";

export type ColorValue = { hex: string; pattern: PatternType };

// ============================================================================
// CONSTANTS
// ============================================================================

/** Grid size: 10×10 = 100 swatches always visible */
const GRID_SIZE = 10;

const COLOR_NAMES: ColorName[] = [
  "slate",
  "gray",
  "zinc",
  "neutral",
  "stone",
  "red",
  "orange",
  "amber",
  "yellow",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "sky",
  "blue",
  "indigo",
  "violet",
  "purple",
  "fuchsia",
  "pink",
  "rose",
];

const COLOR_SHADES: ColorShade[] = [
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
];

/** 22 families × 7 shades = 154 base colors */
const BASE_COLORS = COLOR_NAMES.flatMap((name) =>
  COLOR_SHADES.map((shade) => ({
    id: `${name}-${shade}`,
    hex: (colors[name] as Record<string, string>)[shade],
  }))
);

/** Pattern definitions with CSS overlay styles */
export const PATTERNS: {
  id: PatternType;
  label: string;
  style: React.CSSProperties;
}[] = [
  { id: "solid", label: "Solid", style: {} },
  {
    id: "stripe",
    label: "Stripe",
    style: {
      backgroundSize: "6px 6px",
      backgroundImage:
        "linear-gradient(45deg, rgba(255,255,255,0.12) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.12) 75%, transparent 75%)",
    },
  },
  {
    id: "dots",
    label: "Dots",
    style: {
      backgroundSize: "6px 6px",
      backgroundImage:
        "radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)",
    },
  },
  {
    id: "grid",
    label: "Grid",
    style: {
      backgroundSize: "6px 6px",
      backgroundImage:
        "linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)",
    },
  },
  {
    id: "hatch",
    label: "Hatch",
    style: {
      backgroundSize: "6px 6px",
      backgroundImage:
        "linear-gradient(135deg, rgba(0,0,0,0.2) 25%, transparent 25%, transparent 50%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.2) 75%, transparent 75%)",
    },
  },
];

/** All 770 combinations: 154 colors × 5 patterns */
const ALL_COMBINATIONS = PATTERNS.flatMap((pattern) =>
  BASE_COLORS.map((color) => ({
    id: `${color.id}|${pattern.id}`,
    hex: color.hex,
    pattern: pattern.id,
  }))
);

// ============================================================================
// UTILITIES
// ============================================================================

export function serializeColor(value: ColorValue): string {
  return `${value.hex}|${value.pattern}`;
}

export function deserializeColor(value: string): ColorValue {
  if (!value) {
    return { hex: BASE_COLORS[0].hex, pattern: "solid" };
  }
  // Handle legacy hex-only values (backwards compatibility)
  if (!value.includes("|")) {
    return { hex: value, pattern: "solid" };
  }
  const [hex, pattern] = value.split("|");
  return {
    hex: hex || BASE_COLORS[0].hex,
    pattern: (pattern as PatternType) || "solid",
  };
}

export function getColorStyle(value: ColorValue): React.CSSProperties {
  const pattern = PATTERNS.find((p) => p.id === value.pattern) || PATTERNS[0];
  return { backgroundColor: value.hex, ...pattern.style };
}

/** Get a random available color from the full range (all patterns) */
export function getRandomColor(usedColors: string[]): string {
  const usedSet = new Set(usedColors.map((c) => c.toLowerCase()));
  const available = ALL_COMBINATIONS.filter(
    (c) =>
      !usedSet.has(
        serializeColor({ hex: c.hex, pattern: c.pattern }).toLowerCase()
      )
  );
  if (available.length === 0) {
    return serializeColor({ hex: BASE_COLORS[0].hex, pattern: "solid" });
  }
  const pick = available[Math.floor(Math.random() * available.length)];
  return serializeColor({ hex: pick.hex, pattern: pick.pattern });
}

// ============================================================================
// COMPONENTS
// ============================================================================

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  usedColors?: string[];
}

export const ColorPicker: FC<ColorPickerProps> = ({
  value,
  onChange,
  usedColors = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentValue = deserializeColor(value);
  const usedSet = useMemo(() => {
    const set = new Set(usedColors.map((c) => c.toLowerCase()));
    // Exclude current selection from available pool
    set.add(value.toLowerCase());
    return set;
  }, [usedColors, value]);

  const displayPool = useMemo(() => {
    const pool: { hex: string; pattern: PatternType; id: string }[] = [];
    const target = GRID_SIZE * GRID_SIZE;

    // Add available colors tier by tier until pool is full
    for (const pattern of PATTERNS) {
      if (pool.length >= target) break;

      for (const color of BASE_COLORS) {
        if (pool.length >= target) break;

        const serialized = serializeColor({
          hex: color.hex,
          pattern: pattern.id,
        }).toLowerCase();
        if (!usedSet.has(serialized)) {
          pool.push({
            hex: color.hex,
            pattern: pattern.id,
            id: `${color.id}|${pattern.id}`,
          });
        }
      }
    }

    return pool;
  }, [usedSet]);

  const handleSelect = (hex: string, pattern: PatternType) => {
    onChange(serializeColor({ hex, pattern }));
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className="size-5 rounded-full border border-black/10 ring-2 ring-transparent hover:ring-white/40 transition-shadow focus:outline-none focus:ring-2 focus:ring-ring"
          style={getColorStyle(currentValue)}
          aria-label="Pick color"
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 overflow-hidden"
        align="start"
        sideOffset={4}
      >
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}
        >
          {displayPool.map((item, index) => {
            const isTopLeft = index === 0;
            const isTopRight = index === GRID_SIZE - 1;
            const isBottomLeft = index === displayPool.length - GRID_SIZE;
            const isBottomRight = index === displayPool.length - 1;

            return (
              <button
                key={item.id}
                onClick={() => handleSelect(item.hex, item.pattern)}
                className={cn(
                  "size-6 ring-inset ring-2 ring-transparent hover:ring-white/50 transition-shadow",
                  isTopLeft && "rounded-tl-md",
                  isTopRight && "rounded-tr-md",
                  isBottomLeft && "rounded-bl-md",
                  isBottomRight && "rounded-br-md"
                )}
                style={getColorStyle({ hex: item.hex, pattern: item.pattern })}
                aria-label={item.id}
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** Render a colored label badge */
export const ColorLabel: FC<{
  value: string;
  children: React.ReactNode;
  className?: string;
}> = ({ value, children, className }) => (
  <span
    className={cn(
      "inline-flex items-center px-2 py-0.5 rounded text-sm font-bold text-white",
      className
    )}
    style={getColorStyle(deserializeColor(value))}
  >
    {children}
  </span>
);

export default ColorPicker;
